import React from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import slugify from '@sindresorhus/slugify';
import Button from '@mui/material/Button';
import InstitutionEndpoint from 'endPoints/institutions';
import CoursesEndpoint from 'endPoints/courses';
import useDataListFactory from 'services/dataList';
import LinearProgress from 'components/LinearProgress';
import LocationIcon from 'assets/icons/location-grey.svg?react';
import LinkIcon from 'assets/icons/icon-gg_arrow-up-r-grey.svg?react';
import ScienceIcon from 'assets/icons/streamline_ai-science-spark-solid.svg?react';
import ProfessionalIcon from 'assets/icons/tabler_settings.svg?react';
import ProfessionalBeforeIcon from 'assets/icons/cib_when-i-work.svg?react';
import EducatorHatIcon from 'assets/icons/educator_hat.svg?react';
import getLocale, { getLocalizedPath } from 'helpers/getLocale';
import getProgramLanguageFilter, { getCourseLanguageFilter } from 'helpers/getProgramLanguage';

const COLUMNS_LIMIT_DEFAULT = 2;

const Card = styled('div')(({ color, theme }) => ({
  padding: 25,
  border: `2px solid ${theme.palette.tertiary.main}`,
  borderRadius: 15,
  backgroundColor: color,
  [theme.breakpoints.down('sm')]: {
    padding: 16
  }
}));

const CardTitle = styled(Typography)(() => ({
  fontFamily: 'Unbounded',
  fontSize: 18,
  marginBottom: 15,
  display: 'flex',
  alignItems: 'center',
  gap: 10
}));

const ListItem = styled('div')(({ onClick, theme }) => ({
  borderRadius: 15,
  padding: 15,
  backgroundColor: theme.palette.white.main,
  position: 'relative',
  cursor: onClick ? 'pointer' : 'default',
  [theme.breakpoints.down('md')]: {
    padding: 10
  }
}));

const ListTitle = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.black.main,
  [theme.breakpoints.down('md')]: {
    fontSize: 14
  }
}));

const ListText = styled(Typography)(() => ({
  flex: 10,
  fontSize: 16
}));


const CardsWrapper = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 20,
  marginBottom: 25,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: 25
  }
}));

const ItemsWrapper = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 10,
  marginBottom: 15,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr'
  }
}));

const LoadMoreButton = styled(Button)(({ theme }) => ({
  color: theme.palette.tertiary.main,
  padding: 0,
  minWidth: 0,
  borderRadius: 0,
  textTransform: 'none',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  fontSize: 16,
  lineHeight: '22px',
  margin: '0 auto',
  display: 'flex',
  marginBottom: 0,
  fontWeight: 400,
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline'
  }
}));

const LinkIconStyled = styled(LinkIcon)(() => ({
  position: 'absolute',
  right: 16,
  bottom: 16
}));

const FlexText = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  '& svg': {
    width: 20,
    height: 20,
    flex: 1
  }
}));

const Divider = styled('div')(({ theme }) => ({
  height: 1,
  backgroundColor: theme.palette.primary.main,
  marginTop: 10,
  marginBottom: 10,
  marginLeft: -15,
  marginRight: -15,
  [theme.breakpoints.down('sm')]: {
    marginLeft: -10,
    marginRight: -10
  }
}));

const useDataList = (props, options) => useDataListFactory(props, options);

const InstitutionsComponent = ({ professionInfo, t }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [pagination, setPagination] = React.useState({ first: 1, second: 1, third: 1 });

  // Programs/courses are matched to the occupation via the curated
  // `educationPrograms` / `educationCourses` ref arrays on the occupation
  // doc. Without ids the lists were unfiltered ⇒ visually random.
  // Language: prefer the page's locale, fall back to English.
  const programIds = (professionInfo?.educationPrograms || []).join(',');
  const courseIds = (professionInfo?.educationCourses || []).join(',');
  const programLanguageFilter = React.useMemo(
    () => getProgramLanguageFilter(getLocale()).join(','),
    []
  );
  const courseLanguageFilter = React.useMemo(
    () => getCourseLanguageFilter(getLocale()).join(','),
    []
  );

  // Skip the request entirely when the occupation has no curated refs —
  // otherwise the backend returns an unfiltered list, which is exactly the
  // "looks random" failure mode we're fixing.
  const hasProgramIds = !!programIds;
  const hasCourseIds = !!courseIds;

  const { data: dataFirstColumn, loading: loadingFirstColumn } = useDataList(
    { ...InstitutionEndpoint(), autoLoad: hasProgramIds },
    {
      filters: {
        institutionType: 'university',
        idsIn: programIds,
        language: programLanguageFilter
      }
    }
  );

  const { data: dataSecondColumn, loading: loadingSecondColumn } = useDataList(
    { ...InstitutionEndpoint(), autoLoad: hasProgramIds },
    {
      filters: {
        institutionType: 'college',
        idsIn: programIds,
        language: programLanguageFilter
      }
    }
  );

  const { data: dataThirdColumn, loading: loadingThirdColumn } = useDataList(
    { ...CoursesEndpoint, autoLoad: hasCourseIds },
    {
      perPage: 20,
      filters: {
        idsIn: courseIds,
        language: courseLanguageFilter
      }
    }
  );

  const handleLoadMore = React.useCallback((column) => {
    setPagination((prev) => ({ ...prev, [column]: prev[column] + 1 }));
  }, []);

  const handleRedirectUniversity = React.useCallback(
    (item) => {
      // For courses - open external URL if available
      if (item?.courseUrl) {
        window.open(item.courseUrl, '_blank');
        return;
      }

      // For courses without external URL - navigate to course page
      if (item?.platform && !item?.institution) {
        navigate(getLocalizedPath(`/education/courses/${slugify(item.courseName || '')}/${item._id}`));
        return;
      }

      // For universities/colleges - navigate to education page
      if (item?.institution) {
        navigate(
          getLocalizedPath(
            `/education/${slugify(item.institution.name || '')}/${item.institution._id}`
          )
        );
      }
    },
    [navigate]
  );

  const RenderItem = React.useCallback(
    ({ item }) => {
      // Check if this is a course (has courseName) or education program (has specialties)
      const isCourse = !!item?.courseName;

      // Get the first specialty from the array (backend returns specialties array for programs)
      const specialty = item?.specialties?.[0];

      // Get the first direction for courses
      const direction = item?.directions?.[0];

      // For programs: show institution name as title, for courses: show course name
      const title = isCourse
        ? item?.courseName || ''
        : item?.institution?.name || item?.programName || '';

      // For programs: show program name + specialty info
      const programInfo = !isCourse && item?.programName ? item.programName : null;

      // Get location/platform info
      const location = isCourse
        ? item?.platform?.name || ''
        : item?.institution?.contactInfo?.katottgName || '';

      // Get additional info (degree for programs, duration for courses)
      const additionalInfo = isCourse
        ? item?.courseDuration || ''
        : item?.degree || '';

      return (
        <ListItem onClick={() => handleRedirectUniversity(item)}>
          <ListTitle>{title}</ListTitle>
          <Divider />
          {programInfo ? (
            <FlexText>
              <EducatorHatIcon />
              <ListText>{programInfo}</ListText>
            </FlexText>
          ) : null}
          {specialty ? (
            <FlexText sx={{ mt: programInfo ? 1 : 0 }}>
              <EducatorHatIcon />
              <ListText>
                {specialty?.code} {specialty?.name}
              </ListText>
            </FlexText>
          ) : direction ? (
            <FlexText>
              <EducatorHatIcon />
              <ListText>{direction}</ListText>
            </FlexText>
          ) : null}
          {additionalInfo ? (
            <FlexText sx={{ mt: 1 }}>
              <ListText sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                {additionalInfo}
              </ListText>
            </FlexText>
          ) : null}
          {location ? (
            <FlexText sx={{ mt: 1 }}>
              <LocationIcon />
              <ListText>{location}</ListText>
            </FlexText>
          ) : null}
          <LinkIconStyled />
        </ListItem>
      );
    },
    [handleRedirectUniversity]
  );

  const InstitutionsColumn = React.memo(
    ({ title, columnKey, color, Icon, data }) => (
      <Card color={color}>
        <CardTitle variant="h3">
          {<Icon />}
          {t(title)}
        </CardTitle>
        <ItemsWrapper>
          {data.slice(0, COLUMNS_LIMIT_DEFAULT * pagination[columnKey]).map((item, index) => (
            <RenderItem key={`${item?.institution?._id}-${item?._id || index}`} item={item} />
          ))}
        </ItemsWrapper>
        {data.length > COLUMNS_LIMIT_DEFAULT * pagination[columnKey] && (
          <LoadMoreButton aria-label={t('LoadMore')} onClick={() => handleLoadMore(columnKey)}>
            {t('LoadMoreInstitutions')}
          </LoadMoreButton>
        )}
      </Card>
    ),
    [pagination, t]
  );

  // Check if still loading
  const isLoading = loadingFirstColumn || loadingSecondColumn || loadingThirdColumn;

  // Check if any column has data
  const hasUniversities = (dataFirstColumn || []).length > 0;
  const hasColleges = (dataSecondColumn || []).length > 0;
  const hasCourses = (dataThirdColumn || []).length > 0;
  const hasAnyData = hasUniversities || hasColleges || hasCourses;

  // If still loading, show loading indicator
  if (isLoading) {
    return <LinearProgress />;
  }

  // If no data in any column, hide the entire section
  if (!hasAnyData) {
    return null;
  }

  return (
    <CardsWrapper>
      {hasUniversities && (
        <InstitutionsColumn
          title="InstitutionsListColumn1"
          columnKey="first"
          color={theme.palette.tertiary.pale}
          Icon={ScienceIcon}
          data={dataFirstColumn}
        />
      )}
      {hasColleges && (
        <InstitutionsColumn
          title="InstitutionsListColumn2"
          columnKey="second"
          color={theme.palette.tertiary.pale}
          Icon={ProfessionalIcon}
          data={dataSecondColumn}
        />
      )}
      {hasCourses && (
        <InstitutionsColumn
          title="InstitutionsListColumn3"
          columnKey="third"
          color={theme.palette.primary.pale}
          Icon={ProfessionalBeforeIcon}
          data={dataThirdColumn}
        />
      )}
    </CardsWrapper>
  );
};

export default InstitutionsComponent;
